import { useEffect, useState } from "react";
import { ArrowUpRight, Clock, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/PageHero";

const FILTERS = ["Όλα", "Online", "Δια ζώσης"];

export default function Seminars() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("Όλα");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/content/seminars").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const filtered = filter === "Όλα" ? items : items.filter((s) => s.format === filter);

  return (
    <div data-testid="seminars-page">
      <PageHero
        eyebrow="Σεμινάρια"
        title="Μάθε κάτι καινούργιο."
        text="Σεμινάρια και εκπαιδευτικά προγράμματα για επαγγελματίες, εκπαιδευτικούς και ιδιώτες — online και δια ζώσης."
      />

      <section className="pb-24 sm:pb-32 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 mb-12" data-testid="seminar-filters">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`seminar-filter-${f === "Όλα" ? "all" : f === "Online" ? "online" : "inperson"}`}
                onClick={() => setFilter(f)}
                className={`font-mono2 text-xs tracking-[0.15em] uppercase px-4 py-2 rounded-full border transition-colors duration-300 ${
                  filter === f ? "bg-ink text-cream border-ink" : "border-ink/20 text-ink/70 hover:border-terracotta hover:text-terracotta"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <div className="border-t border-ink/10">
            {filtered.map((s, i) => {
              const [day, ...rest] = (s.date || "").split(" ");
              return (
                <Reveal key={s.id} delay={Math.min(i, 3) * 0.05}>
                  <div
                    className="group grid grid-cols-12 gap-4 items-center py-8 sm:py-10 border-b border-ink/10 transition-colors duration-300 hover:bg-sand/60"
                    data-testid={`seminar-item-${i}`}
                  >
                    <div className="col-span-4 sm:col-span-2 font-mono2">
                      <span className="block text-3xl sm:text-4xl font-bold text-terracotta">{day}</span>
                      <span className="block text-[10px] sm:text-xs tracking-[0.15em] uppercase text-slate2 mt-1">{rest.join(" ")}</span>
                    </div>
                    <div className="col-span-8 sm:col-span-5">
                      <h3 className="font-display text-xl sm:text-2xl font-semibold leading-snug transition-transform duration-500 group-hover:translate-x-2">
                        {s.title}
                      </h3>
                      <p className="text-slate2 text-sm leading-relaxed mt-2 line-clamp-2">{s.description}</p>
                    </div>
                    <div className="col-span-8 col-start-5 sm:col-span-3 sm:col-start-auto flex flex-col gap-2 text-xs sm:text-sm text-slate2">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" /> {s.duration}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="w-3.5 h-3.5" />
                        <span
                          className={`font-mono2 text-[10px] tracking-[0.15em] uppercase px-2.5 py-1 ${
                            s.format === "Online" ? "bg-sage/20 text-[#5c7355]" : "bg-lilac/20 text-[#6a5a8f]"
                          }`}
                        >
                          {s.format}
                        </span>
                      </span>
                      {s.seats && <span className="font-mono2 text-[10px] tracking-wider text-ink/45">{s.seats} θέσεις</span>}
                    </div>
                    <div className="hidden sm:flex col-span-2 justify-end">
                      <a
                        href="mailto:info@noeticalab.gr"
                        data-testid={`seminar-register-${i}`}
                        className="inline-flex items-center gap-1.5 text-sm font-medium border border-ink/25 px-5 py-2.5 rounded-full transition-colors duration-300 hover:bg-terracotta hover:border-terracotta hover:text-cream"
                      >
                        Εγγραφή
                        <ArrowUpRight className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {loaded && filtered.length === 0 && (
            <p className="text-slate2 font-mono2 text-sm tracking-wider py-10" data-testid="seminars-empty">
              Δεν υπάρχουν διαθέσιμα σεμινάρια ακόμα.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
